import { createSlice, type Draft } from '@reduxjs/toolkit';

import type { CertificadoAcessosSentenca } from '../../types/certificadoAcessosSentenca.types';
import {
  baixarPdfCertificadoAcessosSentenca,
  CERTIFICADO_SENTENCA_NAO_PUBLICADA,
  CERTIFICADO_SENTENCA_SEM_PERMISSAO,
  fetchCertificadoAcessosSentenca,
} from './actions';

interface CertificadoAcessosSentencaState {
  certificado: CertificadoAcessosSentenca | null;
  loading: boolean;
  error: string | null;
  semPermissao: boolean;
  naoPublicada: boolean;
  requestId: string | null;
  /**
   * `id` do fetch em curso — NÃO é o `processoId`: na porta do notificado o identificador da rota
   * é a `parteArbitral` (ver `PATH_POR_ORIGEM`). Guardar o id REQUISITADO é o que permite ao
   * seletor recusar o documento de outro alvo nas cinco portas; comparar com
   * `certificado.processoId` funcionaria em quatro e devolveria `null` para sempre na do
   * notificado.
   */
  idSolicitado: string | null;
  pdfBaixando: boolean;
}

const initialState: CertificadoAcessosSentencaState = {
  certificado: null,
  loading: false,
  error: null,
  semPermissao: false,
  naoPublicada: false,
  requestId: null,
  idSolicitado: null,
  pdfBaixando: false,
};

const certificadoAcessosSentencaSlice = createSlice({
  name: 'certificadoAcessosSentenca',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCertificadoAcessosSentenca.pending, (state, action) => {
        state.requestId = action.meta.requestId;
        state.idSolicitado = action.meta.arg.id;
        state.loading = true;
        state.error = null;
        state.semPermissao = false;
        state.naoPublicada = false;
        // Zera o documento anterior: este certificado reflete a situação ATUAL (rn11), e manter a
        // leitura antiga durante o recarregamento mostraria uma entrega já superada como se
        // fosse a vigente.
        state.certificado = null;
      })
      .addCase(fetchCertificadoAcessosSentenca.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.certificado = action.payload as Draft<CertificadoAcessosSentenca>;
      })
      .addCase(fetchCertificadoAcessosSentenca.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        if (action.payload === CERTIFICADO_SENTENCA_SEM_PERMISSAO) {
          state.semPermissao = true;
          return;
        }
        if (action.payload === CERTIFICADO_SENTENCA_NAO_PUBLICADA) {
          state.naoPublicada = true;
          return;
        }
        state.error = action.payload || 'Erro ao carregar o Certificado de Acessos da Sentença';
      })
      .addCase(baixarPdfCertificadoAcessosSentenca.pending, (state) => {
        state.pdfBaixando = true;
      })
      .addCase(baixarPdfCertificadoAcessosSentenca.fulfilled, (state) => {
        state.pdfBaixando = false;
      })
      .addCase(baixarPdfCertificadoAcessosSentenca.rejected, (state) => {
        state.pdfBaixando = false;
      });
  },
});

export const certificadoAcessosSentencaReducer = certificadoAcessosSentencaSlice.reducer;
